import { Component, Input, OnInit, ViewChild, ElementRef, OnChanges, SimpleChanges, Output, EventEmitter  } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import {startWith, map} from 'rxjs/operators';
import { MatAutocompleteTrigger } from '@angular/material';

@Component({
  selector: 'search-dropdown',
  templateUrl: './search.dropdown.component.html',
  styleUrls: ['./search.dropdown.component.css']
})
export class SearchDropdownComponent implements OnInit, OnChanges {

  @ViewChild('searchDropdownInput') inputField: ElementRef;
  @ViewChild('searchDropdownInput', { read: MatAutocompleteTrigger}) autoComplete: MatAutocompleteTrigger;

  // Variables from parent component.
  @Input() control: FormControl;
  @Input() list: Array<any>;
  @Input() displayProperty: string; // If the list is set to an object array, we only show this property
  @Input() returnProperty: string; // If the list is set to an object array, we only return the value of this property
  @Input() inputPlaceholder: string;
  @Input() maxLength: string;
  @Input() checkValueInList: boolean;
  @Output() existsInList = new EventEmitter<any>();
  @Output() existingObject = new EventEmitter<any>();
  @Output() existingStr = new EventEmitter<any>();

  private filteredList: Observable<string[]>;

  constructor() {}

  /**
   * Setup observer on component initialization.
   */
  ngOnInit() {
    /*this.filteredList = this.control.valueChanges.pipe(
      startWith(''),
      map(value => this.filter(value == null ? '' : value))
    )*/
    this.filteredList = this.control.valueChanges.pipe(
      map(value => this.filter(value == null ? '' : value))
    )

    // on page load check for value in list (if boolean callback is used)
    if(this.checkValueInList != undefined && this.checkValueInList === true){
      this.filter(this.control.value);
    }
  }

  /**
   * Handles changes to the variables received from parent component.
   * @param changes SimpleChanges object.
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.list) {
      this.handleListChange(changes.list.currentValue)
    }
  }

  /**
   * Returns a filtered list, containing only the items of 'this.list'
   * that includes the given value.
   * @param value Input value.
   */
  private filter(value: string): string[] {
    if(value == null){
      return this.list;
    }

    let arrayToFilter: Array<any>;
    if (this.displayProperty) {
      // Create an array of the wanted property
      arrayToFilter = this.list.map(item => item[this.displayProperty]);
    }
    else {
      arrayToFilter = this.list;
    }
    
    const filterValue = this.normalizeValue(value);

    if(this.checkValueInList != undefined && this.checkValueInList === true && value !== ''){
      this.existsInList.emit(this.checkValueExists(value));
      this.existingObject.emit(this.getReturnObject(value));
      this.existingStr.emit(this.getReturnStr(value));
    } else if (this.checkValueInList != undefined && this.checkValueInList === true && value === ''){
      this.existsInList.emit(false)
    }
    // if no filterValue provided return the unfiltered array sorted by asc from backend else return filtered array sorted by filterValue
    if(value.trim() === ''){
      return arrayToFilter.filter(item => this.normalizeValue(item).includes(filterValue));
    } else {
      return arrayToFilter.filter(item => this.normalizeValue(item).includes(filterValue))
              .sort((a, b) => this.valueStartsWith(b, filterValue) - this.valueStartsWith(a, filterValue));
    }
  }

  /**
   * Makes the value lowercase to match case-insensitive and to
   * not concern about spaces.
   * @param value A string.
   */
  private normalizeValue(value: string): string {
    return value.toLowerCase().replace(/\s/g, '');
  }

  /**
   * Makes a sorting value lowercase to match case-insensitive and to not be concerned with spaces.
   * Checks if the value provided starts with the filtered value
   * Used for sorting the result array by start of string to display the most relevant options first to the user.
   * @param value Existing string value from array
   * @param filterValue String used to filter the value
   * @returns boolean
   */
  private valueStartsWith(value:string, filterValue:string): any {
    return this.normalizeValue(value).startsWith(filterValue);
  }

  /**
   * Updates the list with the given array.
   * @param currentValue The current value of the list.
   */
  private handleListChange(currentValue: Array<any>): void {    
    this.setList(currentValue);
    this.control.setValue(this.control.value) // Triggers the filter function, avoiding issue of no alternatives showing on list change.
  }

  /**
   * Toggles the dropdown panel. On panel open, the control value is reset to
   * show all alternatives.
   * @param event Click event.
   */
  private togglePanel(event: Event): void {
    event.stopPropagation();
    if (this.autoComplete.panelOpen) {
      this.autoComplete.closePanel();
    }
    else {
      this.control.setValue('');
      this.existsInList.emit(false);
      this.autoComplete.openPanel();
    }
  }

  /**
   * Set the list that should be searchable.
   * @param list An array.
   */
  setList(list: Array<any>): void {
    this.list = list;
  }

  /**
   * Focus the input field.
   */
  focus(): void {
    this.inputField.nativeElement.focus();
  }

  /**
   * Check if the filtered value is present in the array (used for boolean callback)
   * @param value value to check in array
   * @returns true/false
   */
  checkValueExists(value){
    let arrayToCheck: Array<any>;
    if (this.displayProperty) {
      // Create an array of the wanted property
      arrayToCheck = this.list.map(item => item[this.displayProperty]);
      return arrayToCheck.some(valueToFind => valueToFind === value);
    }
    else {
      arrayToCheck = this.list;
      return arrayToCheck.some(valueToFind => valueToFind === value)
    }
  }

  /**
   * Return the filtered object
   * @param value value to get object from
   * @returns the filtered object
   */
  getReturnObject(value) {
    let arrayToCheck: Array<any>;
    if (this.displayProperty) {
      arrayToCheck = this.list.map(item => item[this.displayProperty]);
      if (arrayToCheck.some(valueToFind => valueToFind.toLowerCase() === value.toLowerCase())){ 
        let foudObject = this.list.find(valueToFind => valueToFind[this.displayProperty].toLowerCase() === value.toLowerCase())
        if(this.returnProperty){
          return foudObject[this.returnProperty]
        } else {
          return this.list.find(valueToFind => valueToFind[this.displayProperty] === value)
        }
      }
    }
  }

  /**
   * Return the filtered string
   * @param value Value to get from list of values
   * @returns if present the string value present in the list else the string value as is.
   */
  getReturnStr(value) {
    let arrayToCheck: Array<any>;
    //if displayProperty is set use getReturnObject
    if(this.displayProperty) {
      return this.getReturnObject(value);
    } else {
      // List should only contain strings
      arrayToCheck = this.list;
      //check if value exists in list and return that value
      if(arrayToCheck.some(valueToFind => valueToFind.toLowerCase() === value.toLowerCase())){
        let foundValue = arrayToCheck.find(valueToFind => valueToFind.toLowerCase() === value.toLowerCase());
        return foundValue;
      } else {
        //If the value is not already present in the list return value as is (needed for report functionality)
        return value;
      }
    }
  }
}
